import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth';
import { useLeoEntries, useLeoFilters, useLeoMutations } from '../hooks';
import { LeoDataTable, LeoDialog, LeoMergeDialog } from '../components';
import type { LeoEntry } from '../types';

/**
 * Case 360° (Lot 2) Stage 8 — the LEO Reference directory page: standardized,
 * moderated, searchable list of courts / government bodies / notaries.
 * Curators (`leo:verify`) promote DRAFT → VERIFIED, merge duplicates, and
 * archive entries (soft-delete — references stay readable).
 */
export function LeoPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, resetFilters, setPage, setLimit } = useLeoFilters();

  const { data, isLoading, isError } = useLeoEntries(filters);
  const { verifyMutation, archiveMutation } = useLeoMutations();

  const canCreate = hasAccess('leo', 'create');

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<LeoEntry | null>(null);
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [mergeSource, setMergeSource] = React.useState<LeoEntry | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (entry: LeoEntry) => {
    setEditing(entry);
    setDialogOpen(true);
  };
  const openMerge = (entry: LeoEntry) => {
    setMergeSource(entry);
    setMergeOpen(true);
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);

  const entries = data?.results ?? [];

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('leo.pageTitle')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {canCreate && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {t('leo.add')}
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <LeoDataTable
          controlsContainer={tableControlsEl}
          data={entries}
          total={data?.total ?? 0}
          filters={filters}
          isLoading={isLoading}
          isError={isError}
          onFiltersChange={updateFilter}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onClearFilters={resetFilters}
          onEdit={openEdit}
          onVerify={(entry) => verifyMutation.mutate({ id: entry.id })}
          onMerge={openMerge}
          onArchive={(entry) => archiveMutation.mutate({ id: entry.id })}
          onCreate={canCreate ? openCreate : undefined}
          hasAccess={hasAccess}
          isVerifying={verifyMutation.isPending}
          isArchiving={archiveMutation.isPending}
        />
      </div>

      <LeoDialog open={dialogOpen} onOpenChange={setDialogOpen} entry={editing} />
      <LeoMergeDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        source={mergeSource}
        candidates={entries.filter((e) => !e?.deletedAt && e?.type === mergeSource?.type)}
      />
    </div>
  );
}
